import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import authService from './authService'
import { RootState } from '../index';

interface IinitialState {
    user: object | null
    token: string | null
    isLoggedIn: boolean
    isError: boolean
    isSuccess: boolean
    isLoading: boolean
    isVerified: boolean
    isValidated: boolean
    message: any
}

// Assuming 'persist:root' is the correct key for your Redux Persist configuration
const persistedState = JSON.parse(localStorage.getItem('persist:root') || '{}')
// Access the 'auth' reducer state
const auth = persistedState.auth ? JSON.parse(persistedState.auth) : {};

const initialState: IinitialState = {
    user: auth.user ? auth.user : null,
    token: auth.token ? auth.token : null,
    isLoggedIn: false,
    isError: false,
    isSuccess: false,
    isLoading: false,
    isVerified: false,
    isValidated: false,
    message: '',
}

export const register = createAsyncThunk(
    'auth/register',
    async (user, thunkAPI) => {
        try {
            return await authService.register(user)
        } catch (error: any) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message &&
                    error.response.data.error) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const login = createAsyncThunk('auth/login', async (user, thunkAPI) => {
    try {
        return await authService.login(user)
    } catch (error: any) {
        console.log(error.response.data.error)
        const message =
            (error.response && error.response.data && error.response.data.error) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMe = createAsyncThunk(
    'auth/getMe',
    async (token, thunkAPI) => {
        try {
            console.log('Fetching user with token:', token);
            const response = await authService.getMe(token);
            console.log('Response from getMe:', response.data);
            return response.data; // Ensure this contains { token, user }
        } catch (error: any) {
            console.error('Error in getMe:', error);
            return thunkAPI.rejectWithValue(error.response?.data || 'Unknown error');
        }
    }
);

export const logout = createAsyncThunk<any, any, { state: RootState }>(
    'auth/logout',
    async (thunkAPI) => {
        try {
            return await authService.logout()
        } catch (error: any) {
            const message =
                (error.response && error.response.data && error.response.data.error) ||
                error.message ||
                error.toString() ||
                error.error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        reset: (state) => {
            state.isError = false
            state.isSuccess = false
            state.isLoading = false
            state.message = ''
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMe.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getMe.fulfilled, (state, action) => {
                console.log('getMe fulfilled payload:', action.payload); // Debug payload
                state.isLoading = false;
                state.isSuccess = true;

                // Safely update state
                state.token = action.payload?.token || null;
                state.user = action.payload?.user || null;
                state.isLoggedIn = !!action.payload?.token;

                // Update localStorage for persistence
                localStorage.setItem(
                    'persist:root',
                    JSON.stringify({
                        auth: {
                            ...state,
                            token: action.payload?.token || null,
                            user: action.payload?.user || null,
                            isLoggedIn: !!action.payload?.token,
                        },
                    })
                );
            })
            .addCase(getMe.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                state.user = null;
                state.token = null;
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null
                state.token = null
                state.isLoggedIn = false
            })
            .addCase(register.pending, (state) => {
                state.isLoading = true
            })
            .addCase(register.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.token = action.payload.token
                state.user = action.payload.user
                state.isLoggedIn = true
            })
            .addCase(register.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.user = null
                state.token = null
            })
            .addCase(login.pending, (state) => {
                state.isLoading = true
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.token = action.payload.token
                state.user = action.payload.user
                state.isLoggedIn = true
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.isLoggedIn = false
                state.user = null
                state.token = null
            })
    },
});

export const { reset } = authSlice.actions
export default authSlice.reducer
